import { FC, ReactNode, useMemo } from "react"
import { useSelector } from "react-redux"
import classnames from "classnames"
import {
  Button,
  Container,
  Stack,
  Typography,
  UserIcon
} from "@eventbrite/marmalade"
import Lottie from "react-lottie"

// utils
import {
  customRendererToRemovePTag,
  renderRichText
} from "../../utils/contentful/render_rich_text"
import { goToLoginPage } from "../ProfileMenuBar/ProfileMenuBar"

// types
import { RootState } from "../../store/store"

// styles
import * as styles from "./login.module.scss"

// lottie data
import * as greenLoaderLottieData from "../../icons/lottie/GreenSpinner/Progress loading_circular_Green.json"

type Props = {
  loginHeaderMicrocopy: Queries.HelpArticleQuery["loginHeaderMicrocopy"]
  loginContentMicrocopy: Queries.HelpArticleQuery["loginContentMicrocopy"]
  loginButtonMicrocopy: Queries.HelpArticleQuery["loginButtonMicrocopy"]
  mobileSizeOnly?: boolean
  containerClassName?: string
}

export const LogIn: FC<Props> = ({
  loginButtonMicrocopy,
  loginContentMicrocopy,
  loginHeaderMicrocopy,
  mobileSizeOnly,
  containerClassName
}) => {
  const hasAttemptedLogin = useSelector(
    (state: RootState) => state?.metaData?.hasAttemptedLogin
  )
  const isLoggedIn = useSelector((state: RootState) => state?.userProfile)
    ? true
    : false
  const isLoggingIn = !hasAttemptedLogin && !isLoggedIn

  const loginHeaderMicrocopyRendered = useMemo<ReactNode>(() => {
    return loginHeaderMicrocopy?.microcopyValue?.raw
      ? renderRichText(
          loginHeaderMicrocopy.microcopyValue.raw,
          customRendererToRemovePTag
        )
      : ""
  }, [loginHeaderMicrocopy])
  const loginContentMicrocopyRendered = useMemo<ReactNode>(() => {
    return loginContentMicrocopy?.microcopyValue?.raw
      ? renderRichText(
          loginContentMicrocopy.microcopyValue.raw,
          customRendererToRemovePTag
        )
      : ""
  }, [loginContentMicrocopy])
  const loginButtonMicropyRendered = useMemo<ReactNode>(() => {
    return loginButtonMicrocopy?.microcopyValue?.raw
      ? renderRichText(
          loginButtonMicrocopy.microcopyValue.raw,
          customRendererToRemovePTag
        )
      : ""
  }, [])

  if (isLoggedIn && hasAttemptedLogin) {
    return null
  }

  return (
    <Container
      className={classnames(
        styles.logInContainer,
        mobileSizeOnly ? styles.logInMobileSizeOnlyContainer : "",
        containerClassName
      )}
    >
      <div className={styles.outerWrapper}>
        <div className={styles.iconWrapper}>
          <UserIcon size="xlarge" className={styles?.iconWrapper} />
        </div>

        <Stack className={styles.innerWrapper}>
          <Stack>
            <Typography
              variant="heading-sm"
              as="h3"
              className={styles.headingText}
            >
              {loginHeaderMicrocopyRendered}
            </Typography>

            <Typography variant="body-md" className={styles.loginContentText}>
              {loginContentMicrocopyRendered}
            </Typography>
          </Stack>

          <Stack className={styles.buttonWrapper} space="spacing-md">
            <Button
              size="block"
              variant="primary"
              className={styles.btn}
              onClick={!isLoggingIn ? () => goToLoginPage() : () => null}
            >
              {isLoggingIn ? (
                <Lottie
                  options={{
                    loop: true,
                    autoplay: true,
                    animationData: greenLoaderLottieData,
                    rendererSettings: {
                      preserveAspectRatio: "xMidYMid slice"
                    }
                  }}
                  width={24}
                  height={24}
                />
              ) : (
                loginButtonMicropyRendered
              )}
            </Button>
          </Stack>
        </Stack>
      </div>
    </Container>
  )
}
